/**
 * Tipos de voz do Greening — clique no avatar escolhe a voz (preferência local).
 * `tts` em GET /config = disponibilidade (flag+chave); vários podem ser true.
 * Default de leitura na UI: Greening3 (browser).
 */

export type GreeningVoiceEngine = 'browser' | 'speechify' | 'fish_audio';

export type GreeningDisplayName = 'Greening1' | 'Greening2' | 'Greening3';

/** Chaves de `tts` (disponibilidade) e `active` (hint) em GET /api/greening/voice/config. */
export type GreeningVoiceFlagKey = 'greening1' | 'greening2' | 'greening3';

/** Idioma fixo de todas as versões de voz. */
export const GREENING_VOICE_LANG = 'pt-BR';

/** Paths públicos TTS (gateway → regulatory). Não alterar sem sync no backend. */
export const VOICE_TTS_PATHS = {
  speechifyCarly: '/api/greening/voice/tts/speechify-carly',
  speechifyThiago: '/api/greening/voice/tts/speechify-thiago',
  fishIsabela: '/api/greening/voice/tts/fish-isabela',
} as const;

export interface GreeningVoicePauses {
  commaMs: number;
  semicolonMs: number;
  periodMs: number;
  inlineQuestionMs: number;
  bulletMs: number;
  sectionMs: number;
  questionMs: number;
}

export interface GreeningVoiceProfile {
  id: string;
  displayName: GreeningDisplayName;
  flagKey: GreeningVoiceFlagKey;
  label: string;
  engine: GreeningVoiceEngine;
  lang: string;
  rate: number;
  preferredVoiceNames: string[];
  chunkChars: number;
  pauses: GreeningVoicePauses;
  proxyPath?: string;
  accent: string;
  notes?: string;
}

const PAUSES: GreeningVoicePauses = {
  commaMs: 120,
  semicolonMs: 200,
  periodMs: 280,
  inlineQuestionMs: 450,
  bulletMs: 500,
  sectionMs: 1200,
  questionMs: 2000,
};

export const PADRAO1: GreeningVoiceProfile = {
  id: 'PADRAO1',
  displayName: 'Greening1',
  flagKey: 'greening1',
  label: 'Greening1',
  engine: 'speechify',
  lang: GREENING_VOICE_LANG,
  rate: 1,
  preferredVoiceNames: [],
  chunkChars: 110,
  pauses: PAUSES,
  proxyPath: VOICE_TTS_PATHS.speechifyCarly,
  accent: '#009053',
  notes: 'API neural pt-BR. Áudio efêmero.',
};

export const PADRAO2_FISH_ISABELA: GreeningVoiceProfile = {
  id: 'PADRAO2_FISH_ISABELA',
  displayName: 'Greening2',
  flagKey: 'greening2',
  label: 'Greening2',
  engine: 'speechify',
  lang: GREENING_VOICE_LANG,
  rate: 1,
  preferredVoiceNames: [],
  chunkChars: 110,
  pauses: PAUSES,
  proxyPath: VOICE_TTS_PATHS.speechifyThiago,
  accent: '#2B92AC',
  notes: 'API neural pt-BR. Áudio efêmero.',
};

export const PADRAO3_BROWSER: GreeningVoiceProfile = {
  id: 'PADRAO3_BROWSER',
  displayName: 'Greening3',
  flagKey: 'greening3',
  label: 'Greening3',
  engine: 'browser',
  lang: GREENING_VOICE_LANG,
  rate: 0.9,
  preferredVoiceNames: ['Luciana', 'Francisca', 'Google português do Brasil', 'Google portugues'],
  chunkChars: 110,
  pauses: PAUSES,
  accent: '#0288d1',
  notes: 'speechSynthesis pt-BR.',
};

export const VOICE_PROFILES: Record<string, GreeningVoiceProfile> = {
  PADRAO1,
  PADRAO2_FISH_ISABELA,
  PADRAO3_BROWSER,
};

/** Voz padrão selecionada: Greening1 (Rosana, Speechify). O navegador (Greening3)
 *  deixa de ser opção no picker e fica só como fallback automático se a API falhar. */
export const ACTIVE_VOICE_PROFILE_ID = 'PADRAO1';

export const VOICE_PREFERENCE_KEY = 'greening.voice.preferredProfileId.v2';

export function listVoiceProfiles(): GreeningVoiceProfile[] {
  return [PADRAO1, PADRAO2_FISH_ISABELA, PADRAO3_BROWSER];
}

export function getVoiceProfile(id: string = ACTIVE_VOICE_PROFILE_ID): GreeningVoiceProfile {
  return VOICE_PROFILES[id] || PADRAO3_BROWSER;
}

export function profileForFlagKey(flagKey: GreeningVoiceFlagKey): GreeningVoiceProfile {
  return listVoiceProfiles().find((profile) => profile.flagKey === flagKey) || PADRAO1;
}

export function getPreferredVoiceProfileId(): string {
  if (typeof window === 'undefined') return ACTIVE_VOICE_PROFILE_ID;
  try {
    const stored = window.localStorage.getItem(VOICE_PREFERENCE_KEY);
    // Ignora navegador salvo de versões antigas: agora só Rosana/Thiago são opção.
    if (stored && VOICE_PROFILES[stored] && VOICE_PROFILES[stored].engine !== 'browser') return stored;
  } catch {
    /* storage bloqueado */
  }
  return ACTIVE_VOICE_PROFILE_ID;
}

export function setPreferredVoiceProfileId(id: string): void {
  if (!VOICE_PROFILES[id] || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VOICE_PREFERENCE_KEY, id);
  } catch {
    /* storage bloqueado */
  }
}

function chosenStartVoiceId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(VOICE_PREFERENCE_KEY);
    if (stored === PADRAO1.id || stored === PADRAO2_FISH_ISABELA.id) return stored;
  } catch {
    /* storage bloqueado */
  }
  return null;
}

/** Voz desenhada como ATIVO. Greening3 ou vazio aparecem como Greening1, sem gravar. */
export function displayStartVoiceProfileId(): string {
  return chosenStartVoiceId() || PADRAO1.id;
}

/** No botão de disparo: Greening1 ou Greening2 ficam; Greening3 ou vazio passam a Greening1. */
export function ensureStartVoiceProfileId(): string {
  const chosen = chosenStartVoiceId();
  if (chosen) return chosen;
  setPreferredVoiceProfileId(PADRAO1.id);
  return PADRAO1.id;
}

/**
 * Cadeia de fala: avatar escolhido pelo clique; se API (1/2) indisponível/falhar → Greening3.
 */
export function fallbackChainFor(preferredFlagKey: GreeningVoiceFlagKey): GreeningVoiceProfile[] {
  const preferred = profileForFlagKey(preferredFlagKey);
  if (preferred.engine === 'browser') return [PADRAO3_BROWSER];
  return [preferred, PADRAO3_BROWSER];
}
